import React, { useState } from 'react';

import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { NavLinkButton, Badge, Table, Text, Tooltip } from '@shared/components/bootstrap';
import { Timestamp } from '@shared/components/helpers';
import { UserRole } from '@shared/types/user_role';

import { Panel } from '@admin/components/helpers/panel';
import { Pagination } from '@admin/components/pagination';
import { Spinner } from '@admin/components/spinner';
import { ROLES } from '@admin/config/roles';
import { usePusher } from '@admin/hooks';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  PaginatedIngestionUploadsQuery,
  Ingestion__Upload__State,
  usePaginatedIngestionUploadsQuery,
} from '@admin/schema';

type Upload = PaginatedIngestionUploadsQuery['paginated']['results'][number];

const INGESTION_UPLOAD_STATE_TO_STYLE: Record<Ingestion__Upload__State, 'danger' | 'success' | 'info'> = {
  [Ingestion__Upload__State.Failed]: 'danger',
  [Ingestion__Upload__State.Processed]: 'success',
  [Ingestion__Upload__State.Processing]: 'info',
  [Ingestion__Upload__State.Pending]: 'info',
};

const Status: React.FC<{ upload: Upload }> = ({ upload }) => (
  <Badge style={INGESTION_UPLOAD_STATE_TO_STYLE[upload.state]}>
    {upload.state.toLowerCase()}{' '}
    {upload.state === Ingestion__Upload__State.Processing && <FontAwesomeIcon spin icon={faSpinner} />}
  </Badge>
);

const Row: React.FC<{ upload: Upload }> = ({ upload }) => (
  <tr>
    <td>{upload.id}</td>
    <td>{upload.user.name}</td>
    <td>{upload.processingAt && <Timestamp value={upload.processingAt} format={Timestamp.Format.DateTime} />}</td>
    <td>{upload.processedAt && <Timestamp value={upload.processedAt} format={Timestamp.Format.DateTime} />}</td>
    <td>
      <a className="btn-link" href={upload.spreadsheet.url} target="_blank">
        <strong>{upload.spreadsheet.filename}</strong>
      </a>
      <Text style="muted" tag="div">
        {upload.description}
      </Text>
    </td>
    <td>
      {upload.error ? (
        <Tooltip title={upload.error}>
          <Status upload={upload} />
        </Tooltip>
      ) : (
        <Status upload={upload} />
      )}
    </td>
  </tr>
);

export const List: React.FC = () => {
  useTitle('Ingestions');
  const [page, setPage] = useState<number | undefined>();
  const { data, loading, refetch } = usePaginatedIngestionUploadsQuery({ client, variables: { page: page } });
  const paginated = data?.paginated;

  usePusher<{ id: number }>('ingestion-upload', 'changed', () => {
    refetch();
  });

  if (!ROLES.has(UserRole.Admin)) {
    return null;
  }

  return (
    <Panel>
      <Table responsive striped>
        <thead>
          <tr>
            <th>ID</th>
            <th>Uploader</th>
            <th>Processing at</th>
            <th>Processed at</th>
            <th>File</th>
            <th>State</th>
          </tr>
        </thead>
        <tbody>
          {paginated?.results.map((upload) => (
            <Row key={upload.id} upload={upload}></Row>
          ))}
        </tbody>
      </Table>
      {loading && <Spinner />}
      {paginated && <Pagination pagination={paginated.pagination} onPage={setPage} />}
      <Panel.Footer>
        <Text alignment="right" tag="div">
          <NavLinkButton kind="default" to="/ingestions/new">
            New
          </NavLinkButton>
        </Text>
      </Panel.Footer>
    </Panel>
  );
};
